import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Typography, Button } from "@material-tailwind/react";
import { FiRefreshCw, FiDownload, FiFilter, FiGrid, FiBarChart2, FiList } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';

// Import all our components
import WeekSelector from './filters/WeekSelector';
import OwnerFilter from './filters/OwnerFilter';
import TransactionTypeFilter from './filters/TransactionTypeFilter';
import SummaryCards from './tables/SummaryCards';
import DailyMatrix from './tables/DailyMatrix';
import PayTypeBreakdown from './tables/PayTypeBreakdown';
import TransactionTypeMatrix from './tables/TransactionTypeMatrix';
import ReportTabsBar from './shared/ReportTabsBar';

const REPORT_TABS = [
    { id: 'matrix',    label: 'Daily Matrix',    icon: FiGrid,     description: 'Per-car earnings by date' },
    { id: 'breakdown', label: 'Pay Breakdown',   icon: FiBarChart2, description: 'Earnings by payment type' },
    { id: 'types',     label: 'Type Totals',     icon: FiList,     description: 'Totals per transaction type' },
];

const WeeklyReports = () => {
    const { rootUrl } = require('../../../config/config');
    const { token } = useAuth();

    // State for filters
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedTransType, setSelectedTransType] = useState(null);
    const [activeTab, setActiveTab] = useState('matrix');

    // State for data
    const [matrixData, setMatrixData] = useState(null);
    const [typeMatrixData, setTypeMatrixData] = useState(null);
    const [payTypeData, setPayTypeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showError, setShowError] = useState(false);

    // Fetch matrix data
    const fetchMatrixData = useCallback(async () => {
        if (!selectedWeek) return;

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();

            if (selectedOwner) params.append('owner_code', selectedOwner);
            if (selectedTransType) params.append('transaction_types', selectedTransType);

            const response = await axios.get(
                `${rootUrl}/weekly-summary/${selectedWeek}/matrix?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMatrixData(response.data);
        } catch (err) {
            console.error('Error fetching matrix data:', err);
            setError('Failed to load matrix data. Please try again.');
            setShowError(true);
        } finally {
            setLoading(false);
        }
    }, [rootUrl, token, selectedWeek, selectedOwner, selectedTransType]);

    const fetchTypeMatrixData = useCallback(async () => {
        if (!selectedWeek) return;

        try {
            const params = new URLSearchParams();

            if (selectedOwner) params.append('owner_code', selectedOwner);
            if (selectedTransType) params.append('transaction_types', selectedTransType);

            const response = await axios.get(
                `${rootUrl}/weekly-summary/${selectedWeek}/type-totals?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setTypeMatrixData(response.data);
        } catch (err) {
            console.error('Error fetching type matrix data:', err);
            setError('Failed to load type matrix data. Please try again.');
            setShowError(true);
        }
    }, [rootUrl, token, selectedWeek, selectedOwner, selectedTransType]);

    const fetchPayTypeData = useCallback(async () => {
        if (!selectedWeek) return;

        try {
            const params = new URLSearchParams();

            if (selectedOwner) params.append('owner_code', selectedOwner);
            if (selectedTransType) params.append('transaction_types', selectedTransType);

            const response = await axios.get(
                `${rootUrl}/weekly-summary/${selectedWeek}/daily-breakdown?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Group by transaction type and aggregate
            const groupedData = {};
            response.data.forEach(item => {
                const key = item.transaction_type;
                if (!groupedData[key]) {
                    groupedData[key] = {
                        trans_type: key,
                        type_name: item.transaction_name,
                        total_debit: 0,
                        total_credit: 0,
                        net_amount: 0,
                        transaction_count: 0
                    };
                }
                groupedData[key].total_debit += parseFloat(item.total_debit || 0);
                groupedData[key].total_credit += parseFloat(item.total_credit || 0);
                groupedData[key].net_amount += parseFloat(item.net_amount || 0);
                groupedData[key].transaction_count += 1;
            });

            setPayTypeData(Object.values(groupedData));
        } catch (err) {
            console.error('Error fetching pay type data:', err);
            setError('Failed to load transaction breakdown data. Please try again.');
            setShowError(true);
        }
    }, [rootUrl, token, selectedWeek, selectedOwner, selectedTransType]);

    // Load data when filters change
    useEffect(() => {
        if (selectedWeek) {
            Promise.all([
                fetchMatrixData(),
                fetchTypeMatrixData(),
                fetchPayTypeData()
            ]);
        }
    }, [selectedWeek, fetchMatrixData, fetchTypeMatrixData, fetchPayTypeData]);

    const handleRefresh = () => {
        if (selectedWeek) {
            fetchMatrixData();
            fetchTypeMatrixData();
            fetchPayTypeData();
        }
    };

    const handleExport = () => {
        if (!matrixData || !matrixData.dates || !matrixData.cars) {
            alert('No data available to export');
            return;
        }

        try {
            const { dates, cars, matrix_data, column_totals } = matrixData;

            // Helper function to get cell value (same as in DailyMatrix component)
            const getCellValue = (date, car) => {
                let total = 0;
                if (matrix_data) {
                    Object.values(matrix_data).forEach(transType => {
                        const dateRow = transType.matrix.find(row => row.date === date);
                        if (dateRow && dateRow[car]) {
                            total += dateRow[car] || 0;
                        }
                    });
                }
                return total;
            };

            // Helper function to get day total
            const getDayTotal = (date) => {
                let total = 0;
                cars.forEach(car => {
                    total += getCellValue(date, car);
                });
                return total;
            };

            // Helper function to format currency for CSV
            const formatCurrencyForCSV = (value) => {
                if (!value || value === 0) return '0';
                return value.toFixed(2);
            };

            // Create CSV content
            let csvContent = '';

            // Add title and metadata
            csvContent += `Daily Performance Matrix Report\n`;
            csvContent += `Generated on: ${new Date().toLocaleDateString('en-KE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })}\n`;
            csvContent += `Week: ${selectedWeek}\n`;
            if (selectedOwner) csvContent += `Owner Filter: ${selectedOwner}\n`;
            if (selectedTransType) csvContent += `Transaction Type Filter: ${selectedTransType}\n`;
            csvContent += `Currency: Kenya Shillings (KES)\n\n`;

            // Create header row
            const headers = ['Car / Date', ...dates.map(date => {
                const dateObj = new Date(date);
                return `${dateObj.toLocaleDateString('en-US', { weekday: 'short' })} ${dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            }), 'Car Total'];
            csvContent += headers.join(',') + '\n';

            // Add data rows for each car
            cars.forEach(car => {
                const row = [car];

                // Add daily values
                dates.forEach(date => {
                    const value = getCellValue(date, car);
                    row.push(formatCurrencyForCSV(value));
                });

                // Add car total
                const carTotal = column_totals ? (column_totals[car] || 0) : 0;
                row.push(formatCurrencyForCSV(carTotal));

                csvContent += row.join(',') + '\n';
            });

            // Add daily totals row
            const dailyTotalsRow = ['Daily Total'];
            dates.forEach(date => {
                dailyTotalsRow.push(formatCurrencyForCSV(getDayTotal(date)));
            });
            // Add grand total
            const grandTotal = column_totals ? (column_totals.total || 0) : 0;
            dailyTotalsRow.push(formatCurrencyForCSV(grandTotal));
            csvContent += dailyTotalsRow.join(',') + '\n';

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Create filename with current date and filters
            let filename = `Daily_Performance_Matrix_${selectedWeek}`;
            if (selectedOwner) filename += `_${selectedOwner}`;
            if (selectedTransType) filename += `_${selectedTransType}`;
            filename += `_${new Date().toISOString().split('T')[0]}.csv`;

            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log('Export completed successfully');
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Error exporting data. Please try again.');
        }
    };

    const clearAllFilters = () => {
        setSelectedOwner(null);
        setSelectedTransType(null);
    };

    return (
        <div className="min-h-screen bg-brand-gradient">
            <div className="w-full p-0 sm:p-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-brand-secondary mb-2">Weekly Performance Reports</h1>
                    <div className="w-24 h-1 bg-brand-primary mx-auto rounded-full"></div>
                </div>

                {/* Combined Filter and Controls Container */}
                <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-6">
                    {/* Filter Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                        <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary flex items-center gap-2">
                            <FiFilter className="w-5 h-5 text-brand-primary" />
                            Filters & Settings
                        </Typography>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                onClick={handleRefresh}
                                disabled={loading || !selectedWeek}
                                size="md"
                                className="bg-gradient-to-r from-brand-primary to-brand-primary/80 hover:from-brand-primary/90 hover:to-brand-primary/70 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 px-6 py-2 rounded-xl font-medium"
                            >
                                <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                {loading ? "Refreshing..." : "Refresh Data"}
                            </Button>
                            <Button
                                onClick={handleExport}
                                disabled={!matrixData}
                                size="md"
                                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 px-6 py-2 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiDownload className="w-4 h-4" />
                                Export Report
                            </Button>
                            {(selectedOwner || selectedTransType) && (
                                <Button
                                    size="md"
                                    variant="outlined"
                                    onClick={clearAllFilters}
                                    className="border-2 border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 transition-all duration-300 flex items-center gap-2 px-6 py-2 rounded-xl font-medium shadow-sm hover:shadow-md"
                                >
                                    <span className="w-4 h-4 text-red-500">✕</span>
                                    Clear Filters
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <WeekSelector
                            selectedWeek={selectedWeek}
                            onWeekChange={setSelectedWeek}
                        />

                        <OwnerFilter
                            selectedOwner={selectedOwner}
                            onOwnerChange={setSelectedOwner}
                        />

                        <TransactionTypeFilter
                            selectedType={selectedTransType}
                            onTypeChange={setSelectedTransType}
                        />
                    </div>
                </Card>

                {/* Error Display */}
                <Alerty
                    open={showError}
                    setOpen={setShowError}
                    color="red"
                    message={error || "An error occurred"}
                />

                {/* Main Content */}
                {selectedWeek ? (
                    <div className="space-y-6">
                        {/* Summary Cards — always visible */}
                        <SummaryCards
                            matrixData={matrixData}
                            summaryData={payTypeData}
                            loading={loading}
                        />

                        {/* Tabbed detail views */}
                        <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                            <ReportTabsBar
                                tabs={REPORT_TABS}
                                activeTab={activeTab}
                                onChange={setActiveTab}
                            />

                            {activeTab === 'matrix' && (
                                <DailyMatrix
                                    matrixData={matrixData}
                                    loading={loading}
                                />
                            )}

                            {activeTab === 'breakdown' && (
                                <PayTypeBreakdown
                                    summaryData={payTypeData}
                                    loading={loading}
                                />
                            )}

                            {activeTab === 'types' && (
                                <TransactionTypeMatrix
                                    typeMatrixData={typeMatrixData}
                                    loading={loading}
                                />
                            )}
                        </Card>
                    </div>
                ) : (
                    <Card className="p-12 text-center">
                        <Typography variant="h5" color="blue-gray" className="mb-2">
                            Select a Week to View Reports
                        </Typography>
                        <Typography variant="small" color="gray">
                            Choose a week from the filter above to see detailed performance reports,
                            daily breakdowns, and transaction analysis.
                        </Typography>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default WeeklyReports;
